import { useCallback, useEffect, useMemo, useState, type DependencyList } from 'react';
import type { ConflictoSinConexion, DetalleGrupoDeAcceso, GrupoDeAcceso, ListaLecturas, MotivoLectura, PaqueteDePuerta, ResultadoLectura, ResumenLecturas } from '../../api/ops/pases';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { Contexto } from '../../componentes/Contexto';
import { EstadoCargando, EstadoError, EstadoVacia, Hueco } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { Paginacion } from '../../componentes/Paginacion';
import { Segmentos } from '../../componentes/Segmentos';
import { Tabla } from '../../componentes/Tabla';
import { TarjetaCifra } from '../../componentes/TarjetaCifra';
import { Ventana } from '../../componentes/Ventana';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { enlaceDeReferencia } from '../../logica/navegacion';
import { usePaginacionCursor } from '../../logica/paginacion';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import {
    CONFLICTOS,
    DESHACER_CONFLICTO,
    ESTADO_PAQUETE,
    FILTRO_MOTIVOS,
    MOTIVOS,
    apartadosVisibles,
    colorDeLectura,
    fechaCorta,
    grupoPorDefecto,
    horaCorta,
    lineaDeLectura,
    motivoValido,
    nombreDeLectura,
    notaValida,
    opcionDeGrupo,
    origenDeLectura,
    registroSoloEnCifras,
    type ApartadoPuerta,
} from './reglas';
import './puerta.css';

/**
 * «Puerta» (6.3 Preparar, 6.4 Conflictos, 6.5 Registro; G778, G782): las tablets de la puerta, lo que no cuadró al volver
 * la conexión y quién ha entrado. El selector «Evento o jornada» lee `listAccessGroups` (Pases no depende de Eventos ni de
 * Reservas). La preparación **solo se lee aquí** (G4: se fija en el evento, con «Cambiar en el evento»); «Revocar» un
 * paquete vale también en solo lectura. Apartados en `?apartado=`, el grupo en `?grupo=` y el conflicto de la campana en
 * `?conflicto=` (`/puerta?apartado=conflictos&conflicto=…`). Desde la ficha del evento, `?evento=` elige su grupo.
 */
const TODOS = '';

const AYUDA = [
    { icono: 'package' as const, titulo: 'Un paquete por tablet', texto: 'Cada tablet guarda una copia cifrada con los pases del evento o la jornada, para seguir validando si se cae la conexión.' },
    { icono: 'ticket' as const, titulo: 'Cupo de taquilla', texto: 'Se reparte entre las tablets: la suma nunca pasa del cupo, así que nadie vende de más sin conexión.' },
    { icono: 'git-compare' as const, titulo: 'Conflictos', texto: 'El mismo pase en dos tablets, un pase anulado que entró o una llegada deshecha mientras otra tablet no tenía conexión. Gana la primera lectura por hora.' },
    { icono: 'list-checks' as const, titulo: 'Registro', texto: 'Cada lectura con su color, su motivo, la tablet y quién validó. No se modifica: deshacer una llegada deja su propia fila, sin color.' },
];

/** Carga con su error y su «Reintentar»; la sesión caducada va al marco. */
function useCarga<T>(pedir: () => Promise<T>, deps: DependencyList, onSesionCaducada: () => void) {
    const [datos, setDatos] = useState<T | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    useEffect(() => {
        let vivo = true;
        pedir()
            .then((d) => {
                if (vivo) {
                    setDatos(d);
                }
            })
            .catch((e: unknown) => {
                if (!vivo) {
                    return;
                }
                if (e instanceof ErrorApi && e.codigo === 'unauthenticated') {
                    onSesionCaducada();
                } else {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
            // Al cambiar lo que se pide, vuelve a «Cargando» en lugar de enseñar lo de antes.
            setDatos(null);
            setError(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `pedir` cambia en cada render; mandan `deps` y el intento.
    }, [...deps, intento, onSesionCaducada]);
    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);
    return { datos, error, recargar };
}

export function Puerta(props: PropsPantalla) {
    const { ops, negocio, busqueda, ruta, navegar, movil, onSesionCaducada } = props;
    const permisos = usePermisos();
    const apartados = apartadosVisibles(permisos);
    const activo = apartados.find((a) => a.clave === busqueda.get('apartado')) ?? apartados[0];
    const [ahora] = useState(() => new Date());

    // Los grupos de la última semana en adelante (G782), por inicio de su ventana.
    const desde = useMemo(() => new Date(ahora.getTime() - 7 * 86_400_000).toISOString(), [ahora]);
    const grupos = useCarga(() => ops.listAccessGroups(negocio, { from: desde }, { limit: 100 }).then((l) => l.data), [ops, negocio, desde], onSesionCaducada);
    const lista = useMemo(() => grupos.datos ?? [], [grupos.datos]);
    // «Ver la puerta» de un evento (10.2) llega con `?evento=`: se elige el grupo de ese evento (`ref.id`).
    const deEvento = busqueda.get('evento');
    const pedido = busqueda.get('grupo') ?? (deEvento === null ? null : (lista.find((g) => g.ref.id === deEvento)?.id ?? null));
    const porDefecto = useMemo(() => grupoPorDefecto(lista, ahora), [lista, ahora]);
    const conTodos = activo?.clave !== 'preparar';
    const elegido: string | null = pedido === TODOS && conTodos ? null : (lista.find((g) => g.id === pedido)?.id ?? porDefecto?.id ?? null);
    const grupo = lista.find((g) => g.id === elegido) ?? null;
    const elegir = (id: string) => {
        const p = new URLSearchParams({ ...(activo !== undefined ? { apartado: activo.clave } : {}), grupo: id });
        navegar(`${ruta}?${p.toString()}`, true);
    };
    // Selector «Evento o jornada» (RV Contexto): en ordenador, en la cabecera del marco (G814); en móvil, en su sitio.
    const conSelector = grupos.datos !== null && lista.length > 0;
    const selector = {
        clave: 'grupo',
        etiqueta: 'Evento o jornada',
        valor: elegido ?? TODOS,
        opciones: [...(conTodos ? [{ valor: TODOS, texto: 'Todos los eventos y jornadas' }] : []), ...lista.map((g) => ({ valor: g.id, texto: opcionDeGrupo(g) }))],
        icono: 'ticket' as const,
        onChange: elegir,
    };
    useCabecera({ ayuda: AYUDA, acciones: [], contexto: !movil && conSelector && activo !== undefined ? [selector] : [] });

    if (activo === undefined) {
        return <Hueco seccion="Puerta" icono="door-open" />;
    }

    const ir = (apartado: ApartadoPuerta, extra: Record<string, string> = {}) => {
        const p = new URLSearchParams({ apartado, ...(elegido !== null ? { grupo: elegido } : {}), ...extra });
        navegar(`${ruta}?${p.toString()}`);
    };

    const cuerpo = (() => {
        if (grupos.error !== null) {
            return <EstadoError seccion="la puerta" codigoSoporte={estadoDeError(grupos.error).codigoSoporte} onReintentar={grupos.recargar} movil={movil} />;
        }
        if (grupos.datos === null) {
            return <EstadoCargando seccion="la puerta" />;
        }
        if (activo.clave === 'preparar') {
            return grupo === null ? <EstadoVacia seccion="eventos ni jornadas con puerta" /> : <Preparar {...props} grupo={grupo} irA={ir} />;
        }
        if (activo.clave === 'conflictos') {
            return <Conflictos {...props} grupo={elegido} grupos={lista} />;
        }
        return registroSoloEnCifras(permisos) ? <RegistroEnCifras {...props} grupo={elegido} /> : <Registro {...props} grupo={elegido} />;
    })();

    return (
        <div className="rv-en-pantalla rv-pu">
            <div className="rv-en-barra rv-pu-barra">
                {apartados.length > 1 && (
                    <Segmentos
                        etiqueta="Apartados de la puerta"
                        movil={movil}
                        items={apartados.map((a) => ({ etiqueta: a.etiqueta, icono: a.icono }))}
                        valor={apartados.indexOf(activo)}
                        onCambio={(i) => {
                            const a = apartados[i];
                            if (a !== undefined) {
                                ir(a.clave);
                            }
                        }}
                    />
                )}
                {movil && conSelector && <Contexto movil {...selector} />}
            </div>
            {cuerpo}
        </div>
    );
}

interface PropsApartado extends PropsPantalla {
    grupo: string | null;
}

/** Por qué falló una escritura, en palabras (solo lectura, permiso, conflicto). */
function useFallo(onSesionCaducada: () => void) {
    const avisar = useAvisoBreve();
    return useCallback(
        (e: unknown, texto: string) => {
            if (e instanceof ErrorApi && e.codigo === 'unauthenticated') {
                onSesionCaducada();
                return;
            }
            avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? texto });
        },
        [avisar, onSesionCaducada],
    );
}

// ── 6.3 Preparar ──

function Preparar({ ops, negocio, movil, navegar, destinos, onSesionCaducada, grupo, irA }: PropsPantalla & { grupo: GrupoDeAcceso; irA: (a: ApartadoPuerta) => void }) {
    const avisar = useAvisoBreve();
    const fallo = useFallo(onSesionCaducada);
    const detalle = useCarga<DetalleGrupoDeAcceso>(() => ops.getAccessGroup(negocio, grupo.id), [ops, negocio, grupo.id], onSesionCaducada);
    const paquetes = useCarga<PaqueteDePuerta[]>(() => ops.listDoorPackages(negocio, grupo.id, { limit: 100 }).then((l) => l.data), [ops, negocio, grupo.id], onSesionCaducada);
    const [revocar, setRevocar] = useState<PaqueteDePuerta | null>(null);
    const [motivo, setMotivo] = useState('');
    const [ocupado, setOcupado] = useState(false);

    if (detalle.error !== null) {
        return <EstadoError seccion="la preparación" codigoSoporte={estadoDeError(detalle.error).codigoSoporte} onReintentar={detalle.recargar} movil={movil} />;
    }
    const g = detalle.datos;
    if (g === null) {
        return <EstadoCargando seccion="la preparación" />;
    }
    const a = g.attendance;
    const porLlegar = Math.max(0, a.issued - a.voided - a.inside);
    const enElEvento = enlaceDeReferencia(destinos ?? [], g.ref.type, g.ref.id, 'Cambiar en el evento');
    const vivos = (paquetes.datos ?? []).filter((p) => p.status !== 'revoked' && p.status !== 'wiped' && p.status !== 'expired');

    const confirmarRevocar = () => {
        if (revocar === null) {
            return;
        }
        setOcupado(true);
        ops.revokeDoorPackage(negocio, revocar.id, motivo.trim())
            .then(() => {
                avisar({ tipo: 'ok', texto: 'Paquete revocado: la tablet borrará su copia al conectarse.' });
                setRevocar(null);
                paquetes.recargar();
                detalle.recargar();
            })
            .catch((e: unknown) => {
                fallo(e, 'No se ha podido revocar el paquete.');
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    return (
        <div className="rv-pu-preparar">
            <div className="rv-pu-titulo">
                <h2>{a.inside > 0 ? `Ya han entrado ${String(a.inside)} ${a.inside === 1 ? 'persona' : 'personas'}` : 'La puerta está lista'}</h2>
                <p>{`${g.label} · ${fechaCorta(g.valid_from)} a ${horaCorta(g.valid_to)}`}</p>
            </div>
            <div className="rv-pu-cifras">
                <TarjetaCifra
                    titulo="Por revisar"
                    cifra={String(a.open_conflicts ?? 0)}
                    pie={a.open_conflicts ? 'Conflictos de llegada' : 'Ningún conflicto'}
                    oscura={(a.open_conflicts ?? 0) > 0}
                    enlace={{
                        texto: 'Revisar',
                        accion: () => {
                            irA('conflictos');
                        },
                    }}
                />
                <TarjetaCifra titulo="Pases" cifra={String(a.issued)} pie={`${String(porLlegar)} por llegar · ${String(a.voided)} ${a.voided === 1 ? 'anulado' : 'anulados'}`} />
                <TarjetaCifra titulo="Sin conexión" cifra={String(a.arrivals_offline ?? 0)} pie="Llegadas leídas sin red" />
            </div>
            <section className="rv-pu-tarjeta">
                <header>
                    <strong>Preparación</strong>
                    <span>
                        {g.door
                            ? 'Se aplica a todas las tablets. Se fija en el evento; aquí solo se consulta.'
                            : 'Aún sin preparar: se prepara en el evento, y las tablets descargan el paquete con tiempo.'}
                    </span>
                </header>
                {g.door && (
                    <dl className="rv-pu-datos">
                        <div>
                            <dt>Abre puertas</dt>
                            <dd>{fechaCorta(g.door.door_opens_at)}</dd>
                        </div>
                        <div>
                            <dt>El paquete caduca</dt>
                            <dd>{fechaCorta(g.door.package_expires_at)}</dd>
                        </div>
                        <div>
                            <dt>Cupo de taquilla</dt>
                            <dd>{`${String(g.door.box_office_quota)} entradas`}</dd>
                            <span>{`${String(g.box_office_quota_assigned ?? 0)} repartidas entre las tablets`}</span>
                        </div>
                    </dl>
                )}
                {enElEvento !== null && (
                    <Boton
                        tipo="secundario"
                        icono="arrow-up-right"
                        movil={movil}
                        onClick={() => {
                            navegar(enElEvento.ruta);
                        }}
                    >
                        {enElEvento.etiqueta}
                    </Boton>
                )}
            </section>
            <h3 className="rv-pu-sub">
                Tablets <span>{`${String(vivos.length)} con paquete`}</span>
            </h3>
            {paquetes.error !== null ? (
                <EstadoError seccion="las tablets" codigoSoporte={estadoDeError(paquetes.error).codigoSoporte} onReintentar={paquetes.recargar} movil={movil} />
            ) : paquetes.datos === null ? (
                <EstadoCargando seccion="las tablets" />
            ) : (
                <Tabla
                    etiqueta="Tablets con paquete"
                    movil={movil}
                    vacia={<EstadoVacia seccion="tablets con paquete" />}
                    columnas={[
                        { clave: 'tablet', titulo: 'Tablet' },
                        { clave: 'actualizado', titulo: 'Actualizado' },
                        { clave: 'taquilla', titulo: 'Taquilla' },
                        { clave: 'estado', titulo: 'Estado' },
                    ]}
                    filas={paquetes.datos.map((p) => ({
                        clave: p.id,
                        celdas: {
                            tablet: p.device_name ?? 'Tablet',
                            actualizado: p.last_refresh_at ? fechaCorta(p.last_refresh_at) : 'Sin descargar',
                            taquilla: `${String(p.box_office_used)} de ${String(p.box_office_quota)}`,
                            estado: <Chip estado={ESTADO_PAQUETE[p.status]?.chip ?? 'neutral'}>{ESTADO_PAQUETE[p.status]?.texto ?? p.status}</Chip>,
                        },
                        // «Revocar» vale también en solo lectura (se pierde una tablet a cualquier hora).
                        acciones:
                            p.status === 'revoked' || p.status === 'wiped' || p.status === 'expired' ? undefined : (
                                <Boton
                                    tipo="fantasma"
                                    icono="ban"
                                    movil={movil}
                                    onClick={() => {
                                        setMotivo('');
                                        setRevocar(p);
                                    }}
                                >
                                    Revocar
                                </Boton>
                            ),
                    }))}
                />
            )}
            <Ventana
                abierta={revocar !== null}
                titulo={`Revocar el paquete de ${revocar?.device_name ?? 'la tablet'}`}
                texto="Deja de actualizarse y, al conectarse, borra su copia. Lo que leyó antes se sigue enviando."
                accion="Revocar"
                accionIcono="ban"
                ocupado={ocupado}
                ocupadoTexto="Revocando…"
                inactiva={!motivoValido(motivo)}
                movil={movil}
                onAccion={confirmarRevocar}
                onCerrar={() => {
                    setRevocar(null);
                }}
            >
                <Campo etiqueta="Motivo" valor={motivo} onChange={setMotivo} filas={3} max={500} ayuda="Al menos 10 caracteres. Queda en Actividad." />
            </Ventana>
        </div>
    );
}

// ── 6.4 Conflictos ──

function Conflictos({ ops, negocio, movil, busqueda, onSesionCaducada, grupo, grupos }: PropsApartado & { grupos: readonly GrupoDeAcceso[] }) {
    const avisar = useAvisoBreve();
    const fallo = useFallo(onSesionCaducada);
    const [estado, setEstado] = useState<'open' | 'reviewed'>('open');
    const pag = usePaginacionCursor();
    const lista = useCarga(() => ops.listSyncConflicts(negocio, grupo, estado, { cursor: pag.cursor }), [ops, negocio, grupo, estado, pag.cursor], onSesionCaducada);
    const [abierto, setAbierto] = useState<string | null>(() => busqueda.get('conflicto'));
    const [decision, setDecision] = useState<'keep_first' | 'undo_arrival' | null>(null);
    const [nota, setNota] = useState('');
    const [ocupado, setOcupado] = useState(false);

    const filas = lista.datos?.data ?? [];
    const conflicto = filas.find((c) => c.id === abierto) ?? (movil ? undefined : filas[0]);
    const nombreGrupo = (id: string | undefined) => grupos.find((g) => g.id === id)?.label ?? '';

    const guardar = (c: ConflictoSinConexion) => {
        if (decision === null) {
            return;
        }
        setOcupado(true);
        ops.resolveSyncConflict(negocio, c.id, { decision, note: nota.trim() })
            .then(() => {
                avisar({ tipo: 'ok', texto: 'Revisión guardada.' });
                setDecision(null);
                setNota('');
                setAbierto(null);
                lista.recargar();
            })
            .catch((e: unknown) => {
                fallo(e, 'No se ha podido guardar la revisión.');
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const ficha = (c: ConflictoSinConexion) => {
        const k = CONFLICTOS[c.kind];
        const pasos = [c.winning_scan, ...c.losing_scans];
        return (
            <article className="rv-pu-conflicto">
                <header>
                    <strong>{c.holder_name ?? c.pass_label ?? 'Pase sin nombre'}</strong>
                    <span>{[k.titulo, nombreGrupo(c.access_group_id), fechaCorta(c.created_at)].filter((p) => p !== '').join(' · ')}</span>
                </header>
                <p>{k.texto}</p>
                <h4>Lo que pasó</h4>
                <ol className="rv-pu-pasos">
                    {pasos.map((l, i) => (
                        <li key={l.id}>
                            <span className="rv-pu-hora">{horaCorta(l.device_scanned_at ?? l.server_received_at)}</span>
                            <span>
                                <strong>{`${i === 0 ? 'Entró por' : 'Se leyó otra vez en'} ${l.by?.device_name ?? 'una tablet'}`}</strong>
                                <span>{[l.by?.member_name, l.offline ? 'sin conexión' : null, i === 0 ? 'gana por ser la primera' : 'queda como ámbar'].filter((p) => p !== null && p !== undefined).join(' · ')}</span>
                            </span>
                        </li>
                    ))}
                </ol>
                {c.status === 'reviewed' ? (
                    <p className="rv-pu-revisado">
                        <Icono nombre="check" />
                        {`${c.resolution === 'arrival_undone' ? 'Se deshizo la llegada' : 'Se mantuvo la llegada'}${c.review_note ? `: ${c.review_note}` : ''}`}
                    </p>
                ) : (
                    <>
                        <h4>Qué hacemos</h4>
                        <div role="radiogroup" aria-label="Qué hacemos" className="rv-pu-opciones">
                            {(
                                [
                                    ['keep_first', k.mantener],
                                    ['undo_arrival', DESHACER_CONFLICTO],
                                ] as const
                            ).map(([valor, [titulo, texto]]) => (
                                <button
                                    key={valor}
                                    type="button"
                                    role="radio"
                                    aria-checked={decision === valor}
                                    className="rv-pu-opcion"
                                    onClick={() => {
                                        setDecision(valor);
                                    }}
                                >
                                    <strong>{titulo}</strong>
                                    <span>{texto}</span>
                                </button>
                            ))}
                        </div>
                        <Campo etiqueta="Motivo" valor={nota} onChange={setNota} filas={3} max={500} ayuda="Al menos 10 caracteres. Queda en Actividad." />
                        <Boton
                            icono="check"
                            movil={movil}
                            ocupado={ocupado}
                            ocupadoTexto="Guardando…"
                            disabled={decision === null || !notaValida(nota)}
                            onClick={() => {
                                guardar(c);
                            }}
                        >
                            Guardar la revisión
                        </Boton>
                    </>
                )}
            </article>
        );
    };

    return (
        <div className="rv-pu-conflictos">
            <div className="rv-pu-filtros" role="radiogroup" aria-label="Estado de los conflictos">
                {(
                    [
                        ['open', 'Por revisar'],
                        ['reviewed', 'Revisados'],
                    ] as const
                ).map(([valor, texto]) => (
                    <button
                        key={valor}
                        type="button"
                        role="radio"
                        aria-checked={estado === valor}
                        className="rv-pu-filtro"
                        onClick={() => {
                            setEstado(valor);
                            setAbierto(null);
                            pag.reiniciar();
                        }}
                    >
                        {texto}
                    </button>
                ))}
            </div>
            {lista.error !== null ? (
                <EstadoError seccion="los conflictos" codigoSoporte={estadoDeError(lista.error).codigoSoporte} onReintentar={lista.recargar} movil={movil} />
            ) : lista.datos === null ? (
                <EstadoCargando seccion="los conflictos" />
            ) : filas.length === 0 ? (
                <EstadoVacia seccion={estado === 'open' ? 'conflictos por revisar' : 'conflictos revisados'} />
            ) : (
                <div className="rv-pu-dos">
                    {(!movil || conflicto === undefined) && (
                        <ul className="rv-pu-lista" aria-label="Conflictos">
                            {filas.map((c) => (
                                <li key={c.id}>
                                    <button
                                        type="button"
                                        aria-current={c.id === conflicto?.id}
                                        onClick={() => {
                                            setAbierto(c.id);
                                            setDecision(null);
                                            setNota('');
                                        }}
                                    >
                                        <span className="rv-pu-hora">{horaCorta(c.winning_scan.device_scanned_at ?? c.created_at)}</span>
                                        <span>
                                            <strong>{c.holder_name ?? c.pass_label ?? 'Pase sin nombre'}</strong>
                                            <span>{[CONFLICTOS[c.kind].titulo, nombreGrupo(c.access_group_id)].filter((p) => p !== '').join(' · ')}</span>
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    {conflicto !== undefined && ficha(conflicto)}
                </div>
            )}
            {lista.datos !== null && (
                <Paginacion
                    hayAnterior={pag.hayAnterior}
                    haySiguiente={lista.datos.next_cursor !== null}
                    onAnterior={pag.anterior}
                    onSiguiente={() => {
                        pag.siguiente(lista.datos?.next_cursor);
                    }}
                    movil={movil}
                />
            )}
        </div>
    );
}

// ── 6.5 Registro ──

const COLORES: readonly [ResultadoLectura | null, string][] = [
    [null, 'Todas'],
    ['green', 'Verdes'],
    ['amber', 'Ámbar'],
    ['red', 'Rojas'],
];

function Registro({ ops, negocio, movil, onSesionCaducada, grupo }: PropsApartado) {
    const [color, setColor] = useState<ResultadoLectura | null>(null);
    const [motivo, setMotivo] = useState<MotivoLectura | ''>('');
    const pag = usePaginacionCursor();
    const { reiniciar } = pag;
    useEffect(reiniciar, [grupo, reiniciar]);
    const filtro = useMemo(
        () => ({ ...(grupo !== null ? { access_group_id: grupo } : {}), ...(color !== null ? { result: color } : {}), ...(motivo !== '' ? { reason: motivo } : {}) }),
        [grupo, color, motivo],
    );
    const lista = useCarga<ListaLecturas>(() => ops.listScans(negocio, filtro, { cursor: pag.cursor }), [ops, negocio, filtro, pag.cursor], onSesionCaducada);

    return (
        <div className="rv-pu-registro">
            <div className="rv-pu-herr">
                <div className="rv-pu-filtros" role="radiogroup" aria-label="Color de la lectura">
                    {COLORES.map(([valor, texto]) => (
                        <button
                            key={texto}
                            type="button"
                            role="radio"
                            aria-checked={color === valor}
                            className="rv-pu-filtro"
                            onClick={() => {
                                setColor(valor);
                                reiniciar();
                            }}
                        >
                            {texto}
                        </button>
                    ))}
                </div>
                <label className="rv-pu-selector">
                    <span className="rv-pu-oculto">Motivo</span>
                    <Icono nombre="sliders-horizontal" />
                    <select
                        value={motivo}
                        onChange={(e) => {
                            setMotivo(e.target.value as MotivoLectura | '');
                            reiniciar();
                        }}
                    >
                        <option value="">Todos los motivos</option>
                        {FILTRO_MOTIVOS.map((m) => (
                            <option key={m} value={m}>
                                {MOTIVOS[m].corto}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
            {lista.error !== null ? (
                <EstadoError seccion="el registro" codigoSoporte={estadoDeError(lista.error).codigoSoporte} onReintentar={lista.recargar} movil={movil} />
            ) : lista.datos === null ? (
                <EstadoCargando seccion="el registro" />
            ) : (
                <>
                    <Tabla
                        etiqueta="Registro de lecturas"
                        movil={movil}
                        vacia={<EstadoVacia seccion="lecturas" />}
                        columnas={[
                            { clave: 'hora', titulo: 'Hora', ancho: '90px' },
                            { clave: 'quien', titulo: 'Quién' },
                            { clave: 'tablet', titulo: 'Tablet y quién validó' },
                        ]}
                        filas={lista.datos.data.map((l) => {
                            const c = colorDeLectura(l.result);
                            const origen = origenDeLectura(l);
                            return {
                                clave: l.id,
                                celdas: {
                                    hora: <span className="rv-pu-hora">{horaCorta(l.device_scanned_at ?? l.server_received_at)}</span>,
                                    quien: (
                                        <span className={`rv-pu-lectura rv-pu-lectura--${c.clase}`}>
                                            <span className="rv-pu-marca" aria-hidden="true">
                                                {c.icono !== null && <Icono nombre={c.icono} />}
                                            </span>
                                            <span>
                                                <strong>{nombreDeLectura(l)}</strong>
                                                <span>{lineaDeLectura(l)}</span>
                                            </span>
                                        </span>
                                    ),
                                    tablet: (
                                        <span className="rv-pu-origen">
                                            <strong>{origen.donde}</strong>
                                            <span>{`${origen.como}${l.offline ? ' · sin conexión' : ''}`}</span>
                                        </span>
                                    ),
                                },
                            };
                        })}
                    />
                    <Paginacion
                        hayAnterior={pag.hayAnterior}
                        haySiguiente={lista.datos.next_cursor !== null}
                        onAnterior={pag.anterior}
                        onSiguiente={() => {
                            pag.siguiente(lista.datos?.next_cursor);
                        }}
                        movil={movil}
                    />
                </>
            )}
        </div>
    );
}

/** El Registro del Socio: solo las cifras agregadas (`getScanSummary`), sin detalle por persona. */
function RegistroEnCifras({ ops, negocio, movil, onSesionCaducada, grupo }: PropsApartado) {
    const resumen = useCarga<ResumenLecturas>(() => ops.getScanSummary(negocio, grupo), [ops, negocio, grupo], onSesionCaducada);
    if (resumen.error !== null) {
        return <EstadoError seccion="el registro" codigoSoporte={estadoDeError(resumen.error).codigoSoporte} onReintentar={resumen.recargar} movil={movil} />;
    }
    if (resumen.datos === null) {
        return <EstadoCargando seccion="el registro" />;
    }
    const t = resumen.datos.totals;
    return (
        <div className="rv-pu-registro">
            <div className="rv-pu-cifras">
                <TarjetaCifra titulo="Llegadas" cifra={String(t.arrivals)} pie={`${String(t.undone)} deshechas`} />
                <TarjetaCifra titulo="Verdes" cifra={String(t.green)} pie={`${String(t.amber)} ámbar · ${String(t.red)} rojas`} />
                <TarjetaCifra titulo="Sin conexión" cifra={String(t.offline ?? 0)} pie="Lecturas sin red" />
            </div>
            <Tabla
                etiqueta="Lecturas por motivo"
                movil={movil}
                vacia={<EstadoVacia seccion="lecturas" />}
                columnas={[
                    { clave: 'motivo', titulo: 'Motivo' },
                    { clave: 'n', titulo: 'Lecturas', alinear: 'derecha' },
                ]}
                filas={resumen.datos.by_reason.map((r) => ({ clave: r.reason, celdas: { motivo: MOTIVOS[r.reason].corto, n: String(r.count) } }))}
            />
        </div>
    );
}
